import type { ReactNode } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';

import type { ProductTileViewModel } from '../types/ProductTileViewModel';
import { ProductTileView } from './ProductTileView';

/** Product grid shows 3 full tiles + a thin partial 4th — same peek as My Life. */
const VISIBLE_TILE_SLOTS = 3.2;
/** Matches the rail's `gap-2` (8px); 3 gaps sit within the visible slots. */
const TILE_GAP_PX = 8;
/**
 * Responsive tile width: fit {@link VISIBLE_TILE_SLOTS} slots (with their 3
 * inter-tile gaps) across the rail's content width so the 4th tile always peeks.
 * Pure CSS `%` of the flex container — no JS measurement.
 */
export const TILE_CELL_WIDTH = `calc((100% - ${TILE_GAP_PX * 3}px) / ${VISIBLE_TILE_SLOTS})`;

interface Props {
  products: ProductTileViewModel[];
  /** Live bio context — threaded to each tile to build its PDP href at render. */
  bioContext: BioContext;
  /** Bio base font family, applied to each tile's text. */
  fontFamily: string;
  /** Per-product owner kebab (editable variant only), top-right of each tile. */
  renderProductMenu?: (productId: string) => ReactNode;
  /**
   * Dashboard-only: open a tile's PDP in-place. When supplied, each tile becomes
   * a `<button>`; otherwise the crawlable soft-nav `<Link>` (visitor).
   */
  onOpenProduct?: (productId: string) => void;
  /**
   * Forwarded to each tile — `false` puts the product name BELOW a fully-rounded
   * thumbnail instead of overlaying it (see {@link ProductTileView}). Defaults to
   * the overlaid form the footer-bearing rack card uses.
   */
  nameOverlay?: boolean;
}

/**
 * The product tiles of a rack rail — container-less (a fragment), so it drops
 * straight into the rail's scroll container as flex children. Shared by the
 * rack's first page, its horizontal load-more batches, and the static (editable)
 * rail, so every tile renders identically.
 */
export function CollectionRackTiles({
  products,
  bioContext,
  fontFamily,
  renderProductMenu,
  onOpenProduct,
  nameOverlay,
}: Props) {
  return (
    <>
      {products.map(product => (
        <div
          key={product.id}
          className="relative shrink-0"
          style={{ width: TILE_CELL_WIDTH, scrollSnapAlign: 'start' }}
        >
          <ProductTileView
            product={product}
            bioContext={bioContext}
            fontFamily={fontFamily}
            onOpen={onOpenProduct ? () => onOpenProduct(product.id) : undefined}
            // Owner menu occupies the top-right corner — the tile shifts its
            // type-badge stack below it (QA: badges hid behind the kebab).
            hasCornerMenu={renderProductMenu != null}
            nameOverlay={nameOverlay}
          />
          {renderProductMenu ? (
            <div className="absolute right-1 top-1 z-[9]">
              {renderProductMenu(product.id)}
            </div>
          ) : null}
        </div>
      ))}
    </>
  );
}
